import React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { FPSResponse, FPSEmployee, FPSAlert } from "@/types/payroll-validator-demo";

type Props = {
  response: FPSResponse | null;
};

const formatCurrency = (value?: string | number | null) => {
  if (value === null || value === undefined) return "—";
  const asNumber = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(asNumber)) return String(value);
  return `£${asNumber.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const FPSEmployeeAnalysis: React.FC<Props> = ({ response }) => {
  if (!response?.employees?.length) return null;

  return (
    <div className="space-y-3 pt-2">
      {response.employees.map((employee, index) => (
        <EmployeeCard key={`${employee.ni_number || index}-${index}`} employee={employee} />
      ))}
    </div>
  );
};

type EmployeeCardProps = {
  employee: FPSEmployee;
};

const EmployeeCard: React.FC<EmployeeCardProps> = ({ employee }) => (
  <Card className="border border-white/10 bg-[#0f1117] text-slate-100">
    <CardContent className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          <p className="text-base font-semibold text-white">
            {employee.full_name ||
              [employee.first_name, employee.last_name].filter(Boolean).join(" ") ||
              "Employee"}
          </p>
          <p className="text-sm text-slate-400">
            {employee.ni_number ? `NI: ${employee.ni_number}` : "NI not supplied"}
          </p>
        </div>
        {employee.has_benefits_in_kind ? (
          <Badge
            variant="outline"
            className="border-orange-400/40 bg-orange-500/15 text-orange-50"
          >
            BIK detected
          </Badge>
        ) : (
          <Badge variant="outline" className="border-white/20 bg-white/10 text-slate-100">
            No BIK
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <InfoRow label="Tax code" value={employee.tax_code || "—"} />
        <InfoRow label="NI letter" value={employee.ni_letter || "—"} />
        <InfoRow label="Pay frequency" value={employee.pay_frequency || "—"} />
        <InfoRow
          label="Approx. monthly salary"
          value={formatCurrency(employee.approximate_monthly_salary)}
        />
        <InfoRow
          label="Pension type"
          value={employee.pension_scheme_type || "—"}
          valueClass="capitalize"
        />
        <InfoRow
          label="BIK total"
          value={
            employee.has_benefits_in_kind
              ? formatCurrency(employee.benefits_total)
              : "—"
          }
        />
      </div>

      {employee.alerts && employee.alerts.length > 0 && (
        <div className="space-y-2 pt-2">
          {employee.alerts.map((alert, idx) => (
            <AlertCard key={`${alert.alert_code}-${idx}`} alert={alert} />
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

type AlertCardProps = {
  alert: FPSAlert;
};

const AlertCard: React.FC<AlertCardProps> = ({ alert }) => {
  const severityStyles: Record<string, string> = {
    critical: "border-red-500/30 bg-red-500/10 text-red-100",
    medium: "border-amber-500/30 bg-amber-500/10 text-amber-100",
    info: "border-blue-500/30 bg-blue-500/10 text-blue-100",
  };
  const titleStyles: Record<string, string> = {
    critical: "text-red-50",
    medium: "text-amber-50",
    info: "text-blue-50",
  };
  const descStyles: Record<string, string> = {
    critical: "text-red-100/90",
    medium: "text-amber-100/90",
    info: "text-blue-100/90",
  };

  return (
    <Alert className={`border ${severityStyles[alert.severity] || severityStyles.info}`}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle className={titleStyles[alert.severity] || titleStyles.info}>
        {alert.title}
      </AlertTitle>
      <AlertDescription className={descStyles[alert.severity] || descStyles.info}>
        {alert.description}
        {(alert.previous_value !== null || alert.current_value !== null) && (
          <span className="mt-1 block text-xs">
            Previous: {formatCurrency(alert.previous_value)} → Current:{" "}
            {formatCurrency(alert.current_value)}
          </span>
        )}
      </AlertDescription>
    </Alert>
  );
};

type InfoRowProps = {
  label: string;
  value: string;
  valueClass?: string;
};

const InfoRow: React.FC<InfoRowProps> = ({ label, value, valueClass }) => (
  <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2">
    <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
    <span className={`text-sm font-semibold text-slate-100 ${valueClass ?? ""}`}>{value}</span>
  </div>
);

export default FPSEmployeeAnalysis;

